import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import Card from "../layout/Card";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const statusOptions = [
  { id: "all", name: "All" },
  { id: "pending", name: "Pending" },
  { id: "succeeded", name: "Succeeded" },
  { id: "failed", name: "Failed" },
];

const getStatusColor = (status) => {
  if (status === "succeeded") return "success";
  if (status === "failed") return "error";
  if (status === "pending") return "warning";
  return "default";
};

const statementPreview = (statements) => {
  if (!Array.isArray(statements) || statements.length === 0) return "-";
  const first = String(statements[0]?.sql || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!first) return "-";
  return first.length > 120 ? `${first.slice(0, 120)}...` : first;
};

const SqlRecoveryManager = () => {
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const [userIdsText, setUserIdsText] = useState("");
  const [statementsText, setStatementsText] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState(1000);
  const [resetVersion, setResetVersion] = useState(1);
  const [expiresAt, setExpiresAt] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [fullModalOpen, setFullModalOpen] = useState(false);

  const pageCount = useMemo(() => {
    if (!recordsPerPage || recordsPerPage <= 0) return 1;
    const pages = Math.ceil(total / recordsPerPage);
    return pages > 0 ? pages : 1;
  }, [recordsPerPage, total]);

  const loadEntries = async (
    nextPage = pageNumber,
    nextStatus = statusFilter,
  ) => {
    setLoadingList(true);

    try {
      const filter = {};
      if (nextStatus && nextStatus !== "all") {
        filter.lastStatus = nextStatus;
      }

      const result = await executeAction({
        entityName: "SqlRecovery",
        action: "listEntries",
        data: {
          pageNumber: nextPage,
          recordsPerPage,
          filter,
        },
      });

      if (result?.isSuccessful && result.data) {
        setRows(Array.isArray(result.data.records) ? result.data.records : []);
        setTotal(Number(result.data.total || 0));
        return;
      }

      setRows([]);
      setTotal(0);
      setErrorMessage(result?.message || "Failed to load SQL Recovery entries");
    } catch (err) {
      setRows([]);
      setTotal(0);
      setErrorMessage(err.message || "Failed to load SQL Recovery entries");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadEntries(0, statusFilter);
  }, [statusFilter, recordsPerPage]);

  const handleCreateEntries = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!String(userIdsText || "").trim()) {
      setErrorMessage("Paste at least one userId");
      return;
    }

    if (!String(statementsText || "").trim()) {
      setErrorMessage("Paste at least one SQL statement");
      return;
    }

    setSubmitting(true);

    try {
      const result = await executeAction({
        entityName: "SqlRecovery",
        action: "createBatch",
        data: {
          userIds: userIdsText,
          name,
          description,
          priority: Number(priority),
          resetVersion: Number(resetVersion),
          expiresAt: expiresAt || null,
          statementsText,
        },
      });

      if (!result?.isSuccessful || !result.data) {
        setErrorMessage(
          result?.message || "Failed to create SQL Recovery entries",
        );
        return;
      }

      const createdCount = Number(result.data.createdCount || 0);
      const invalidCount = Array.isArray(result.data.invalidUserIds)
        ? result.data.invalidUserIds.length
        : 0;
      const missingCount = Array.isArray(result.data.missingUserIds)
        ? result.data.missingUserIds.length
        : 0;

      const message = `Created ${createdCount} SQL Recovery entries (invalid IDs: ${invalidCount}, missing users: ${missingCount})`;
      setSuccessMessage(message);
      showMessage(message, "success");

      setPageNumber(0);
      await loadEntries(0, statusFilter);
    } catch (err) {
      setErrorMessage(err.message || "Failed to create SQL Recovery entries");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusTable = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Created</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Statements</TableCell>
            <TableCell>Last Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id || row._id}>
              <TableCell>
                {row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}
              </TableCell>
              <TableCell>{row.name || "-"}</TableCell>
              <TableCell>
                {row.userName || row.userId || (row.isGlobal ? "GLOBAL" : "-")}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.lastStatus || "-"}
                  color={getStatusColor(row.lastStatus)}
                />
              </TableCell>
              <TableCell>
                {Number(row.appliedVersion || 0)}/
                {Number(row.resetVersion || 0)}
              </TableCell>
              <TableCell>{row.priority ?? "-"}</TableCell>
              <TableCell title={statementPreview(row.statements)}>
                {Array.isArray(row.statements) ? row.statements.length : 0} -{" "}
                {statementPreview(row.statements)}
              </TableCell>
              <TableCell>{row.lastError || "-"}</TableCell>
            </TableRow>
          ))}
          {!loadingList && rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={8}
                align="center"
              >
                No SQL Recovery entries found
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Card
      title="SQL Recovery"
      sx={{ minWidth: 900, flex: 1 }}
    >
      <Stack spacing={2}>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {successMessage ? (
          <Alert severity="success">{successMessage}</Alert>
        ) : null}

        <Grid
          container
          spacing={2}
          sx={{ padding: "20px" }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              multiline
              minRows={6}
              label="User IDs"
              placeholder="Paste one or more userIds separated by commas, spaces, or new lines"
              value={userIdsText}
              onChange={(event) => setUserIdsText(event.target.value)}
            />
          </Grid>

          <Grid
            size={12}
            sx={{ gridColumn: "1 / -1" }}
          >
            <TextField
              fullWidth
              multiline
              minRows={10}
              label="SQL Statements"
              placeholder="Paste SQL statements separated by semicolon"
              value={statementsText}
              onChange={(event) => setStatementsText(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Name (optional)"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Description (optional)"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Reset Version"
              value={resetVersion}
              onChange={(event) => setResetVersion(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Expires At (optional)"
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
        >
          <Button
            variant="contained"
            onClick={handleCreateEntries}
            disabled={submitting}
          >
            {submitting ? "Creating..." : "Create SQL Recovery Entries"}
          </Button>
        </Stack>

        <Box
          sx={{
            borderTop: "1px solid #ddd",
            pt: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6">SQL Recovery Status</Typography>
            <Stack
              direction="row"
              spacing={1}
            >
              <Button
                variant="outlined"
                onClick={() => setFullModalOpen(true)}
              >
                Open Full Screen List
              </Button>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => {
                    setPageNumber(0);
                    setStatusFilter(event.target.value);
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={option.id}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 130 }}>
                <InputLabel>Rows</InputLabel>
                <Select
                  value={recordsPerPage}
                  label="Rows"
                  onChange={(event) => {
                    setPageNumber(0);
                    setRecordsPerPage(Number(event.target.value));
                  }}
                >
                  {[10, 20, 50, 100].map((size) => (
                    <MenuItem
                      key={size}
                      value={size}
                    >
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={() => loadEntries(pageNumber, statusFilter)}
                disabled={loadingList}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          {renderStatusTable()}

          <Stack
            direction="row"
            justifyContent="flex-end"
            mt={1}
          >
            <Pagination
              page={pageNumber + 1}
              count={pageCount}
              onChange={(_, value) => {
                const nextPage = value - 1;
                setPageNumber(nextPage);
                loadEntries(nextPage, statusFilter);
              }}
            />
          </Stack>
        </Box>

        <Dialog
          open={fullModalOpen}
          onClose={() => setFullModalOpen(false)}
          fullScreen
        >
          <DialogTitle>SQL Recovery Entries</DialogTitle>
          <DialogContent>
            <Stack spacing={1}>
              <Typography variant="body2">
                Full-screen status view for SQL Recovery records.
              </Typography>
              {renderStatusTable()}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => loadEntries(pageNumber, statusFilter)}
              disabled={loadingList}
            >
              Refresh
            </Button>
            <Button onClick={() => setFullModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Card>
  );
};

export default SqlRecoveryManager;
